import React, { useState } from "react";
import { ChevronRight, Eye, Film, Headset } from "lucide-react";
import MemorialRibbon from "@/components/MemorialRibbon";

const BIO = {
  stanley: { focus: "Acoustic cry for help · spiritual resilience · signalling through the drywall breach." },
  brian: { focus: "Flashlight beam sweep · voice localization · the wrist-to-wrist extraction." },
};

function Card({ pov, selected, onSelect, onGuidedStudy }) {
  const tint = pov.tint;
  const active = selected === pov.id;
  return (
    <div className="tab-rise" style={{ width: 350 }}>
      <button
        data-testid={`pov-select-${pov.id}-button`}
        onClick={() => onSelect(pov.id)}
        style={{
          textAlign: "left", cursor: "pointer", width: "100%", padding: 0, borderRadius: "8px 8px 0 0", overflow: "hidden",
          borderTop: active ? `2px solid ${tint}` : `1px solid ${tint}55`, borderLeft: active ? `2px solid ${tint}` : `1px solid ${tint}55`, borderRight: active ? `2px solid ${tint}` : `1px solid ${tint}55`, borderBottom: "none",
          background: `linear-gradient(180deg, ${tint}14, rgba(13,14,17,0.9))`, backdropFilter: "blur(16px)",
          boxShadow: active ? `0 0 0 1px ${tint}, 0 24px 70px rgba(0,0,0,0.6)` : `0 14px 44px rgba(0,0,0,0.55)`,
          transition: "all 0.25s ease", transform: active ? "translateY(-6px) scale(1.02)" : "none",
        }}
      >
        <div style={{ height: 7, background: tint }} />
        <div style={{ padding: "24px 26px 22px" }}>
          <div style={{ fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", color: tint, marginBottom: 9 }}>{pov.floor}</div>
          <h2 className="font-serif-q" style={{ margin: "0 0 5px", color: "#f8f9fa", fontSize: 29, fontWeight: 700, lineHeight: 1.08 }}>
            {pov.name.split(" ").map((w, i) => (
              <span key={i} style={{ display: "block" }}>{w}</span>
            ))}
          </h2>
          <div style={{ fontSize: 12.5, color: "#9aa0ac", marginBottom: 16 }}>{pov.role}</div>
          <p style={{ fontSize: 13, color: "#c2c6cf", lineHeight: 1.55, margin: "0 0 12px", minHeight: 62 }}>{BIO[pov.id].focus}</p>
          <div style={{ fontSize: 12, color: tint }}>{pov.interaction}</div>
        </div>
      </button>
      <div style={{ display: "flex", borderRadius: "0 0 8px 8px", overflow: "hidden", borderBottom: active ? `2px solid ${tint}` : `1px solid ${tint}55`, borderLeft: active ? `2px solid ${tint}` : `1px solid ${tint}55`, borderRight: active ? `2px solid ${tint}` : `1px solid ${tint}55`, borderTop: `1px solid ${tint}33` }}>
        <button
          data-testid={`pov-enter-${pov.id}-button`}
          onClick={() => onSelect(pov.id)}
          className="gold-btn crisis-btn"
          style={{ flex: 2, padding: "12px", cursor: "pointer", fontSize: 12, borderRadius: 0, border: "none" }}
        >
          Enter Path <span className="crisis-tag" style={{ color: "#ff3b30", fontWeight: 800 }}>(Crisis Mode)</span>
        </button>
        <button
          data-testid={`pov-study-${pov.id}-button`}
          onClick={() => onGuidedStudy(pov.id)}
          className="font-serif-q"
          style={{ flex: 1.15, padding: "12px 8px", cursor: "pointer", fontSize: 11.5, fontStyle: "italic", color: "#c9a24b", background: "rgba(13,14,17,0.82)", border: "none", borderLeft: `1px solid ${tint}33`, letterSpacing: "0.02em" }}
          title="Explore the building and testimony at your own pace — no timer, no collapse"
        >
          Explore Path (Guided Study)
        </button>
      </div>
    </div>
  );
}

function ObserverCard({ selected, onSelect }) {
  const active = selected === "observer";
  const gold = "#c9a24b";
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 7 }}>
      <button
        data-testid="pov-select-observer-button"
        onClick={() => onSelect("observer")}
        className="tab-rise"
        style={{
          cursor: "pointer", width: 520, maxWidth: "88vw", padding: "14px 22px", borderRadius: 999,
          border: active ? `2px solid ${gold}` : "1px solid rgba(201,162,75,0.4)",
          background: active ? "rgba(201,162,75,0.14)" : "rgba(13,14,17,0.7)", backdropFilter: "blur(14px)",
          transition: "all 0.25s ease", display: "flex", alignItems: "center", gap: 14,
        }}
      >
        <div style={{ width: 40, height: 40, borderRadius: 999, border: `1px solid ${gold}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, background: "rgba(201,162,75,0.1)" }}>
          <Eye size={19} color={gold} />
        </div>
        <div style={{ textAlign: "left" }}>
          <div className="font-serif-q" style={{ color: "#f2c85b", fontSize: 18, fontWeight: 600, lineHeight: 1.1 }}>
            The Observer <span style={{ fontSize: 10, letterSpacing: "0.18em", color: gold, textTransform: "uppercase", fontFamily: "Outfit" }}>· Universal</span>
          </div>
          <div style={{ fontSize: 11.5, color: "#b9b3a4" }}>360° orbit · X-Ray ghosted walls · egress-vector analysis</div>
        </div>
      </button>
      <div className="font-serif-q" style={{ maxWidth: 520, textAlign: "center", color: gold, fontSize: 12.5, fontStyle: "italic", lineHeight: 1.4, opacity: 0.85 }}>
        A spatial analysis of the structural geometry and the convergence of two paths.
      </div>
    </div>
  );
}

export default function POVPicker({ visible, povs, onStart, onGuidedStudy, onGuidedTour, onEnterVR, vrSupported }) {
  const [selected, setSelected] = useState(null);
  const stanley = povs.find((p) => p.id === "stanley");
  const brian = povs.find((p) => p.id === "brian");
  return (
    <div
      data-testid="pov-picker"
      style={{
        position: "absolute", inset: 0, opacity: visible ? 1 : 0, transition: "opacity 1s ease",
        pointerEvents: visible ? "auto" : "none", zIndex: 15,
      }}
    >
      <div style={{ position: "absolute", inset: 0, background: "rgba(7,10,20,0.55)", pointerEvents: "none" }} />

      {/* Masthead — standard absolute position at the very top; does NOT scroll */}
      <div
        data-testid="picker-masthead"
        style={{
          position: "absolute", top: 0, left: 0, right: 0, zIndex: 4, display: "flex",
          alignItems: "center", justifyContent: "flex-start", gap: 12, padding: "18px 12px 20px 22px",
          background: "linear-gradient(180deg, rgba(7,10,20,0.96) 0%, rgba(7,10,20,0.7) 78%, rgba(7,10,20,0))",
          pointerEvents: "none",
        }}
      >
        <img src="/brand/tab_seal.png" alt="TAB" data-testid="tab-seal-masthead" style={{ width: 46, height: 46, borderRadius: 999, boxShadow: "0 0 0 1px rgba(201,162,75,0.45)" }} />
        <div style={{ textAlign: "left" }}>
          <div style={{ fontSize: 10, letterSpacing: "0.3em", textTransform: "uppercase", color: "#c9a24b", marginBottom: 3 }}>Select Your Perspective</div>
          <h1 className="font-serif-q" style={{ margin: 0, color: "#fff", fontSize: "clamp(22px,3.2vw,36px)", fontWeight: 600, lineHeight: 1 }}>9/11: The Immaculate Salvation</h1>
        </div>
      </div>

      {/* Scrollable content — starts ~100px below the fixed masthead */}
      <div
        className="picker-scroll"
        style={{
          position: "absolute", inset: 0, overflowY: "auto", display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "flex-start", gap: 26,
          paddingLeft: 20, paddingRight: 20, paddingBottom: 40,
        }}
      >
      <div style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 22 }}>
        <div style={{ display: "flex", gap: 26, flexWrap: "wrap", justifyContent: "center" }}>
          {stanley && <Card pov={stanley} selected={selected} onSelect={setSelected} onGuidedStudy={onGuidedStudy} />}
          {brian && <Card pov={brian} selected={selected} onSelect={setSelected} onGuidedStudy={onGuidedStudy} />}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 14, width: 520, maxWidth: "88vw" }}>
          <span style={{ flex: 1, height: 1, background: "rgba(201,162,75,0.25)" }} />
          <span style={{ fontSize: 10, letterSpacing: "0.24em", textTransform: "uppercase", color: "#7e8594" }}>or observe</span>
          <span style={{ flex: 1, height: 1, background: "rgba(201,162,75,0.25)" }} />
        </div>
        <ObserverCard selected={selected} onSelect={setSelected} />
      </div>

      {/* Three primary actions — 15px gap keeps them clear of each other on mobile */}
      <div style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 15 }}>
        <button
          data-testid="start-experience-button"
          disabled={!selected}
          onClick={() => selected && onStart(selected)}
          className="gold-btn"
          style={{ padding: "15px 40px", borderRadius: 4, fontSize: 14, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 9 }}
        >
          {selected === "observer" ? "Enter the Observatory" : "Begin the Descent"} <ChevronRight size={17} />
        </button>

        {/* Immersive VR entry — TAB gold, always visible on every device */}
        <button
          data-testid="enter-immersive-vr-button"
          onClick={() => onEnterVR && onEnterVR(selected)}
          title={vrSupported ? "Launch the immersive WebXR session (Vision Pro / Quest 3)" : "No headset detected — enters the spatial experience"}
          style={{
            padding: "14px 36px", borderRadius: 4, cursor: "pointer", fontSize: 13.5, fontWeight: 700,
            letterSpacing: "0.12em", textTransform: "uppercase", color: "#10120f",
            background: "linear-gradient(180deg,#f7d477,#c9a24b)",
            border: "1px solid #f2c85b",
            boxShadow: "0 0 0 1px rgba(201,162,75,0.5), 0 12px 34px rgba(201,162,75,0.22)",
            display: "inline-flex", alignItems: "center", gap: 10, transition: "transform 0.2s ease, box-shadow 0.2s ease",
          }}
        >
          <Headset size={17} strokeWidth={2.4} /> Enter Immersive VR
        </button>

        {/* Fourth mode — hands-free cinematic narrated tour */}
        <button
          data-testid="guided-tour-button"
          onClick={() => onGuidedTour && onGuidedTour()}
          className="font-serif-q"
          style={{ padding: "12px 30px", borderRadius: 999, fontSize: 13, fontStyle: "italic", cursor: "pointer", color: "#c9a24b", background: "rgba(13,14,17,0.72)", border: "1px solid rgba(201,162,75,0.45)", display: "inline-flex", alignItems: "center", gap: 9, backdropFilter: "blur(12px)" }}
        >
          <Film size={15} /> Guided Tour (Hands-Free)
        </button>
      </div>

      {/* Persistent commemorative footer — keeps the mourning ribbon visible below the
          anniversary line on the selection screen too (outside the fading LandingCover) */}
      <div style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 8, marginTop: 16 }}>
        <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 11, letterSpacing: "0.28em", textTransform: "uppercase" }}>
          September 11, 2001 · 25th Anniversary
        </div>
        <MemorialRibbon size={58} />
      </div>
      </div>
    </div>
  );
}
